import { FC, useState, ChangeEvent, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MuiAlert from "@material-ui/lab/Alert";
import { useStore, connect } from "src/store/connect";
import { buildFlowInput } from "src/services/flows/models";
import { useDeployFlow } from "src/services/flows/hooks";
import { ReactComponent as WhatsAppIcon } from "src/assets/whatsapp.svg";
import { WHATSAPP_BASE_URL, WHATSAPP_PHONE_NUMBER } from "src/config/constants";
import ErrorMessages from "./ErrorMessages";
import mixpanel from "mixpanel-browser";

interface DeployFlowProps {
  onClose: () => void;
  isOpen: boolean;
}

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const DeployFlow: FC<DeployFlowProps> = ({
  isOpen,
  onClose,
}: DeployFlowProps) => {
  const classes = useStyles();
  const state = useStore();
  const createDeployFlowQuery = useDeployFlow();
  const [deployKey, setDeployKey] = useState<string>("");
  const [inboundMessage, setInboundMessage] = useState<string>("ssample");

  useEffect(() => {
    state.flow.isDeployed = false
    state.flow.inboundMessage = ""
    createDeployFlowQuery.reset()
  }, [state.flow, state.flow.edges, state.flow.nodes]);

  const onAddNewErrorMessage = () => {
    state.addErrorMessage("");
    mixpanel.track('added_error_message')
  };

  const OnChangeKeyInput = (event: ChangeEvent<HTMLInputElement>) => {
    setDeployKey(event.target.value);
  };

  const onDeploy = () => {
    const flowInput = buildFlowInput(deployKey, state.flow);
    const output = createDeployFlowQuery.mutate(flowInput, {
      onSuccess: (data) => {
        // @ts-ignore
        const inboundMessage = data.data.greeting
        state.flow.isDeployed = true
        state.flow.inboundMessage = inboundMessage
        mixpanel.track('flow_deployed', {
          ...state.getFlowNodeCountPerType()
        })

        setInboundMessage(inboundMessage)
      },
    });
  };

  const onCloseWrapper = (elementPressed : string) => {
    mixpanel.track('deploy_window_closed', { button_pressed: elementPressed })
    onClose()
  }

  const onCloseSnackbar = () => {
    createDeployFlowQuery.reset()
  }

  const onOpenInWhatsapp = () => {
    const url = `${WHATSAPP_BASE_URL}/${WHATSAPP_PHONE_NUMBER}?text=${inboundMessage}`
    mixpanel.track('whatsapp_link_clicked', {button_used: 'deploy_modal'})

      window.open(encodeURI(url), '_blank');
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={onClose}>
      {createDeployFlowQuery.isLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} className={classes.circularProgress} />
          <Typography className={classes.title}>
            Estamos desplegando tu conversación
          </Typography>
        </div>
      ) : (
        <div>
          <Snackbar
            open={createDeployFlowQuery.isError}
            autoHideDuration={4000}
            onClose={onCloseSnackbar}
          >
            <Alert severity="error">
              Ocurrió un error, el flujo no pudo ser desplegado.
            </Alert>
          </Snackbar>
          {createDeployFlowQuery.isSuccess ? (
            <div>
              <AppBar className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                  <Button
                    className={classes.textButton}
                    variant="text"
                    onClick={onClose}
                  >
                    Salir
                  </Button>
                  <IconButton
                    className={classes.closeIcon}
                    edge="start"
                    onClick={onClose}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <div className={classes.modalContent}>
                <div className={classes.card}>
                  <span className={classes.cardTagColored} />
                  <Typography className={classes.optionOrder}>
                    Número telefónico asociado
                  </Typography>
                  <Typography className={classes.phoneNumber}>
                    +1 (619) 658-6276
                  </Typography>
                </div>
                <Typography className={classes.title}>
                  ¡Listo! Desplegamos tu conversación
                </Typography>
                <Typography className={classes.paragraph}>
                  Solo tienes que hacerle saber a tus usuarios que te pueden
                  escribir. Inicia una conversación con el siguiente mensaje:
                </Typography>
                <Typography className={classes.paragraph} onClick={() => {navigator.clipboard.writeText(inboundMessage)}}>
                  {inboundMessage}
                </Typography>
                <Button className={classes.whatsAppButton} variant="contained" onClick={onOpenInWhatsapp}>
                  <WhatsAppIcon className={classes.whatsAppIcon} />
                  <Typography> Ver en Whatsapp </Typography>
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <AppBar className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                  <Button
                    className={classes.textButton}
                    variant="text"
                    onClick={() => onCloseWrapper('text')}
                  >
                    Guardar y salir
                  </Button>
                  <Button
                    className={classes.secondaryButton}
                    variant="contained"
                    onClick={onDeploy}
                    color="primary"
                  >
                    Desplegar
                  </Button>
                  <IconButton
                    className={classes.closeIcon}
                    edge="start"
                    onClick={() => onCloseWrapper('x-icon')}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <div className={classes.modalContent}>
                <Typography className={classes.title}>
                  Ingresa la clave para desplegar tu flujo
                </Typography>
                <input
                  type="password"
                  className={classes.input}
                  placeholder={"Clave de despligue"}
                  onChange={OnChangeKeyInput}
                  required
                />
                <Typography className={classes.title}>
                  Configura tus mensajes para errores
                </Typography>
                <Typography className={classes.paragraph}>
                  Te recomendamos configurar un mínimo de 2 mensajes para cuando
                  tus usuarios respondan algo que tu bot no pueda reconocer.
                </Typography>
                <ErrorMessages />
                <button
                  className={classes.newAnswer}
                  onClick={onAddNewErrorMessage}
                >
                  Nueva respuesta
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

DeployFlow.defaultProps = {};

export default connect(DeployFlow);

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
    backgroundColor: "#FFF",
    boxShadow: "none",
    borderBottom: "1px solid #CDD5E0",
  },
  toolbar: {
    alignItems: "center",
    justifyContent: "flex-end",
  },
  textButton: {
    background: "none",
    outline: "none",
    textTransform: "none",
    textDecoration: "underline",
    fontSize: "16px",
    fontWeight: 500,
    marginRight: "20px",
  },
  secondaryButton: {
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 6px 0 rgba(68, 83, 226, 0.29)",
    fontSize: "14px",
    fontWeight: 500,
    color: "#ffffff",
    marginRight: "20px",
    width: "180px",
  },
  whatsAppButton: {
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 6px 0 rgba(68, 83, 226, 0.29)",
    backgroundColor: "#37ee8c",
    fontSize: "14px",
    fontWeight: 600,
    marginRight: "14px",
    width: "317px",
    padding: "10px",
    marginTop: "36px",

    "&:hover": {
      backgroundColor: "#37ee8c",
    },
  },
  closeIcon: {
    color: "#1D262C",
  },
  modalContent: {
    margin: "52px 15px 160px 160px",
  },
  title: {
    fontWeight: 600,
    fontSize: "21px",
    lineHeight: "31px",
    marginBottom: "10px",
  },
  paragraph: {
    width: "586px",
    lineHeight: "32px",
  },
  newAnswer: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#0091ff",
    textDecoration: "underline",
    fontFamily: "Poppins",
    background: "none",
    border: "none",
    cursor: "pointer",
    margin: "1rem 0 0 0",
  },
  input: {
    width: "582px",
    background: "#dd99",
    padding: "9px 5px 9px 10px",
    borderRadius: "2.5px",
    border: "solid 1px #cdd5e0",
    backgroundColor: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Poppins",
    margin: "10px 0 30px 0",

    "&:focus": {
      outline: "none",
      border: "solid 1px #cdd5e0",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    height: "60vh",
  },
  circularProgress: {
    marginBottom: "24px",
  },
  whatsAppIcon: {
    marginRight: "10px",
  },
  card: {
    margin: "30px 0 36px 0",
    display: "grid",
    width: "582px",
    gridTemplateColumns: "0.5fr 10fr",
    alignItems: "start",
    background: "#FFFFFF",
    border: "1px solid #EFF2F8",
    borderRadius: "8px",
    padding: "24px",
  },
  cardTagColored: {
    width: "8px",
    height: "38px",
    borderRadius: "2.5px",
    backgroundColor: "#0091ff",
  },
  optionOrder: {
    margin: "10px 8px 0 24px",
    fontSize: "14px",
    fontWeight: 600,
  },
  phoneNumber: {
    gridColumn: "2",
    margin: "10px 8px 0 24px",
    fontSize: "24px",
    fontWeight: 600,
  }
}));
